import { memo, useCallback, type SyntheticEvent } from 'react';
import { MoreHoriz } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useModals } from 'utils/hooks';
import { Typography } from '@mui/material';
import { ItemWrapper, ModalContentWrapper, Wrapper } from './NameArray.styles';
import { ActionModal } from 'components/mui/common/ActionModal';
import type { ColumnOverflowMode, FormItemUiDefinition } from '../../../../types/formItem.ts';
import { getUiDefinitionStyles } from '../../../../utils/styles.ts';

interface Props {
  value: string[];
  maxWidth?: string;
  asColumns?: boolean;
  formItemUiDefinition?: FormItemUiDefinition;
  overflowMode?: ColumnOverflowMode;
  onEdit?: () => void;
}

export const NameArray = memo(({ value, maxWidth, asColumns = false, formItemUiDefinition, overflowMode, onEdit }: Props) => {
  const uiDefinitionStyles = getUiDefinitionStyles(formItemUiDefinition);

  const { t } = useTranslation();
  const [modals, openModal, closeModal] = useModals({
    selected_users: false,
  });

  const handleModalOpen = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      e.preventDefault();
      openModal('selected_users')();
    },
    [openModal]
  );

  const handleEdit = useCallback(() => {
    try {
      closeModal('selected_users')();
      onEdit?.();
    } catch (error) {
      openModal('selected_users')();
    }
  }, [closeModal, onEdit, openModal]);

  if (!value || value.length === 0 || !Array.isArray(value)) return <span className="empty-component-content"></span>;

  return (
    <>
      <Wrapper columns={asColumns} overflowMode={overflowMode}>
        {(overflowMode ? value : value.slice(0, 3)).map((name, index) => (
          <ItemWrapper onClick={handleModalOpen} isClickable maxWidth={maxWidth} key={index} uiDefinitionStyles={uiDefinitionStyles}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'inherit' }}>
              {name}
            </Typography>
          </ItemWrapper>
        ))}
        {!overflowMode && value.length > 3 && (
          <ItemWrapper onClick={handleModalOpen} isClickable>
            <MoreHoriz />
          </ItemWrapper>
        )}
      </Wrapper>
      <ActionModal
        open={modals?.selected_users}
        onClose={closeModal('selected_users')}
        onCancel={closeModal('selected_users')}
        onSubmit={onEdit ? handleEdit : undefined}
        submitText={onEdit ? t('common.edit') : undefined}
        title={t('table.header.detail')}>
        <ModalContentWrapper>
          {(value || [])?.map((name, index) => (
            <ItemWrapper key={index}>
              <Typography variant="subtitle2">{name}</Typography>
            </ItemWrapper>
          ))}
        </ModalContentWrapper>
      </ActionModal>
    </>
  );
});
